import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileCheck, Users, AlertTriangle, BookOpen, Eye, CheckCircle, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';
const GoAMLServices = () => {
  usePageSeo({
    title: 'goAML Compliance Services in UAE | AML Reporting',
    description: 'Expert UAE goAML registration, AML/CFT compliance, suspicious transaction reporting and ongoing monitoring services by WPAA.',
    path: '/services/goaml-compliance'
  });

  const faqs = [
    {
      q: 'What is goAML and who must register?',
      a: 'goAML is the UAE Financial Intelligence Unit (FIU) reporting portal. Designated Non-Financial Businesses and Professions (DNFBPs) — including real estate brokers, dealers in precious metals/stones, auditors, accountants, corporate service providers, and certain legal/insurance entities — are required to register on goAML and file STRs/SARs as part of UAE AML/CFT compliance.'
    },
    {
      q: 'What happens if my business does not register on goAML?',
      a: 'Failure to register on goAML or comply with AML/CFT obligations can lead to significant financial penalties (often starting from AED 50,000 and increasing for repeat offences), licence suspension, and reputational damage. The UAE has been actively enforcing AML rules since 2022.'
    },
    {
      q: 'What is the difference between STR and SAR?',
      a: 'STR (Suspicious Transaction Report) is filed when you suspect a specific transaction is linked to money laundering or terrorism financing. SAR (Suspicious Activity Report) is broader — it covers suspicious activity or behaviour that may not yet be tied to a specific transaction. Both are filed through the goAML portal.'
    },
    {
      q: 'How often must we conduct AML training for staff?',
      a: 'UAE regulations require regular AML/CFT awareness training for all relevant staff — typically at least once per year, plus additional training when policies are updated or new staff are onboarded. We provide structured training programmes aligned with UAE compliance expectations.'
    },
    {
      q: 'Can WPAA file goAML reports on our behalf?',
      a: 'We help you build the right internal capability — registration, policies, MLRO support, and training. STR/SAR filings must be made by your appointed Money Laundering Reporting Officer (MLRO) through your goAML account. We provide advisory and review support, but the formal filing remains the entity\'s responsibility under UAE law.'
    },
    {
      q: 'How long does goAML registration take?',
      a: 'Standard goAML registration typically takes 2–4 weeks, depending on the completeness of documentation and FIU response times. We accelerate the process by preparing all required documentation correctly the first time and managing follow-ups with the authority.'
    }
  ];

  const services = [
    {
      icon: FileCheck,
      title: 'goAML Registration & Setup',
      items: [
        'Assistance with company registration on the goAML portal',
        'User creation, role assignment, and system configuration',
        'Support with FIU profile setup and compliance alignment'
      ]
    },
    {
      icon: Shield,
      title: 'Policy & Compliance Framework',
      items: [
        'AML/CFT policy drafting aligned with UAE regulations',
        'Risk-Based Approach (RBA) implementation',
        'Internal control procedures for reporting entities'
      ]
    },
    {
      icon: AlertTriangle,
      title: 'STR / SAR Reporting Support',
      items: [
        'Guidance on identifying suspicious activities',
        'STR/SAR preparation and submission assistance',
        'Internal reporting workflows and documentation templates'
      ]
    },
    {
      icon: Eye,
      title: 'Risk Assessment & EWRA',
      items: [
        'Enterprise-Wide Risk Assessment (EWRA) preparation',
        'Customer risk classification frameworks',
        'Risk scoring models for DNFBPs and SMEs'
      ]
    },
    {
      icon: BookOpen,
      title: 'AML Documentation & Record Keeping',
      items: [
        'KYC/CDD file structure setup',
        'Compliance registers and reporting logs',
        'Audit-ready AML documentation'
      ]
    },
    {
      icon: Users,
      title: 'Staff Training & Awareness',
      items: [
        'goAML awareness sessions for employees',
        'AML reporting responsibilities and escalation procedures',
        'Training calendars aligned with UAE compliance expectations'
      ]
    },
    {
      icon: CheckCircle,
      title: 'Ongoing Compliance Monitoring',
      items: [
        'Periodic review of AML controls',
        'Support during Ministry of Economy inspections',
        'Remediation action plans and compliance updates'
      ]
    }
  ];

  const clientTypes = [
    {
      title: 'DNFBPs',
      description: 'Consultancies, Corporate Service Providers, Real Estate, Precious Metals, and other Designated Non-Financial Businesses and Professions'
    },
    {
      title: 'Free Zone and Mainland Companies',
      description: 'Businesses operating across UAE jurisdictions requiring AML compliance'
    },
    {
      title: 'New & Dormant Entities',
      description: 'Newly established or dormant UAE entities requiring comprehensive AML setup'
    }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-goaml-service"
        data={serviceSchema({
          name: 'goAML Compliance Services in UAE',
          description:
            'goAML registration, AML/CFT policy development, suspicious transaction reporting (STR/SAR) and ongoing AML compliance monitoring for UAE businesses.',
          path: '/services/goaml-compliance',
          serviceType: 'AML Compliance Services'
        })}
      />
      <JsonLd
        id="ld-goaml-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'goAML Compliance', path: '/services/goaml-compliance' }
        ])}
      />
      <JsonLd id="ld-goaml-faq" data={faqSchema(faqs)} />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4">
              <span className="bg-teal-100 text-teal-700 text-sm font-semibold px-4 py-2 rounded-full">
                AML/CFT Compliance
              </span>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              goAML Compliance & Reporting Services
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-4">
              Supporting UAE businesses, DNFBPs, and regulated entities in meeting their Anti-Money Laundering (AML) and Counter-Terrorism Financing (CFT) obligations through structured goAML compliance and reporting solutions.
            </p>
            <p className="text-lg text-gray-700 font-medium">
              Aligned with Ministry of Economy and UAE Financial Intelligence Unit (FIU) requirements
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">Our Mission</h2>
            <p className="text-teal-50 text-lg leading-relaxed">
              Our services are designed to help companies establish a compliant framework, reduce regulatory risk, and maintain proper reporting standards under UAE AML laws. We focus on practical, regulator-aligned solutions — not just documentation.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our goAML Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive solutions covering every aspect of AML/CFT compliance and goAML reporting
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                      <Icon className="text-teal-600 group-hover:text-white transition-colors" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <ul className="space-y-2">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                          <CheckCircle size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We Support */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Who We Support
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Serving diverse entities across the UAE with specialized AML compliance needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {clientTypes.map((client, index) => (
              <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="text-teal-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{client.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{client.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-2 border-teal-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Approach</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              We focus on <strong className="text-teal-600">practical, regulator-aligned solutions</strong> — not just documentation. Our goal is to ensure your business maintains continuous compliance, operational clarity, and peace of mind while meeting UAE AML obligations.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="text-teal-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Practical Solutions</h4>
                <p className="text-sm text-gray-600">Real-world implementation that works</p>
              </div>
              <div className="text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="text-teal-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Continuous Compliance</h4>
                <p className="text-sm text-gray-600">Ongoing monitoring and support</p>
              </div>
              <div className="text-center">
                <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="text-teal-600" size={28} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Peace of Mind</h4>
                <p className="text-sm text-gray-600">Confidence in regulatory compliance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose White Paper for goAML Compliance?</h2>
              <div className="space-y-4">
                {[
                  'Expert knowledge of UAE AML/CFT regulations and FIU requirements',
                  'Practical, implementation-focused approach beyond documentation',
                  'Support throughout the entire compliance lifecycle',
                  'Experienced team with DNFBP and regulated entity expertise',
                  'Proactive monitoring and remediation support',
                  'Ministry of Economy inspection readiness'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-teal-50 transition-colors">
                    <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop&q=75&fm=webp" loading="lazy"
                alt="AML Compliance"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about UAE goAML registration, AML/CFT compliance, and reporting obligations.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                  data-testid={`goaml-faq-${index}`}
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-teal-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'Audit Services in UAE', description: 'External and internal audit aligned with UAE AML obligations.', path: '/audit-services-uae', accent: 'green' },
          { title: 'Business Advisory', description: 'Risk and compliance advisory beyond AML reporting.', path: '/business-advisory-uae', accent: 'emerald' },
          { title: 'UAE FIU Regulation No. 1 of 2026', description: 'PSTRs, suspension and freezing orders — what reporting entities must prepare.', path: '/knowledge-academy/uae-fiu-regulation-1-2026-suspicious-transactions-freezing-funds', accent: 'indigo' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Ensure goAML Compliance?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Let our AML experts help you establish a robust compliance framework aligned with UAE regulations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?service=goaml">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg group">
                Schedule Consultation
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
            <a href="tel:+971501219341">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-6 text-lg">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoAMLServices;
